import type { TourProgressAdapter } from '@klonup/design-system';

const STORAGE_KEY = 'klonup:onboarding-progress';

interface StoredProgress {
  seen: string[];
  completed: string[];
}

/**
 * Persiste el progreso de onboarding en localStorage distinguiendo "visto" de "completado".
 * Estructura: `{ seen: string[], completed: string[] }`. Un tour completado se considera también
 * visto (completar implica haber visto), de ahí que `hasSeen` consulte la unión de ambos.
 */
export class LocalStorageTourProgressAdapter implements TourProgressAdapter {
  private seen: Set<string>;
  private completed: Set<string>;

  constructor() {
    const loaded = this.load();
    this.seen = loaded.seen;
    this.completed = loaded.completed;
  }

  private load(): { seen: Set<string>; completed: Set<string> } {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { seen: new Set(), completed: new Set() };
      const parsed: unknown = JSON.parse(raw);
      if (parsed === null || typeof parsed !== 'object') {
        return { seen: new Set(), completed: new Set() };
      }
      const obj = parsed as Partial<StoredProgress>;
      const seen = Array.isArray(obj.seen)
        ? obj.seen.filter((x): x is string => typeof x === 'string')
        : [];
      const completed = Array.isArray(obj.completed)
        ? obj.completed.filter((x): x is string => typeof x === 'string')
        : [];
      return { seen: new Set(seen), completed: new Set(completed) };
    } catch {
      return { seen: new Set(), completed: new Set() };
    }
  }

  private save(): void {
    const payload: StoredProgress = {
      seen: [...this.seen],
      completed: [...this.completed],
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  hasCompleted(tourId: string): boolean {
    return this.completed.has(tourId);
  }

  hasSeen(tourId: string): boolean {
    return this.seen.has(tourId) || this.completed.has(tourId);
  }

  /** Ids vistos (incluye completados) actualmente en localStorage — para sembrar otros adapters. */
  snapshotSeen(): string[] {
    return [...new Set([...this.seen, ...this.completed])];
  }

  /** Ids completados actualmente en localStorage — para sembrar otros adapters. */
  snapshotCompleted(): string[] {
    return [...this.completed];
  }

  markSeen(tourId: string): void {
    this.seen.add(tourId);
    this.save();
  }

  markCompleted(tourId: string): void {
    // Completar implica haber visto: ambos sets se marcan.
    this.seen.add(tourId);
    this.completed.add(tourId);
    this.save();
  }

  reset(): void {
    this.seen.clear();
    this.completed.clear();
    localStorage.removeItem(STORAGE_KEY);
  }
}
